import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { api, type ValidateInviteDetails } from '@/api/client'
import { Button, Alert } from '@/components/ui'
import { Building2, UserPlus, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'

type InviteState =
  | { status: 'loading' }
  | { status: 'valid'; details: ValidateInviteDetails }
  | { status: 'error'; error: string; details?: ValidateInviteDetails }
  | { status: 'accepting' }
  | { status: 'accepted'; orgSlug: string; orgName: string }

export function AcceptInvitePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isAuthenticated, isLoading: authLoading } = useAuth()

  const token = searchParams.get('token')
  const [state, setState] = useState<InviteState>({ status: 'loading' })

  // Validate the invite token on mount
  useEffect(() => {
    if (!token) {
      setState({ status: 'error', error: 'No invite token provided' })
      return
    }

    const validateInvite = async () => {
      try {
        const response = await api.orgs.validateInvitation(token)

        if (response.valid && response.details) {
          setState({ status: 'valid', details: response.details })
        } else {
          const errorMessages: Record<string, string> = {
            not_found: 'This invite link is invalid or has already been used.',
            expired: 'This invite has expired.',
            already_used: 'This invite has already been accepted.',
            org_inactive: 'The organization is no longer active.',
          }
          setState({
            status: 'error',
            error: errorMessages[response.error || ''] || 'Invalid invite',
            details: response.details || undefined,
          })
        }
      } catch (err) {
        console.error('Failed to validate invite:', err)
        setState({ status: 'error', error: 'Failed to validate invite. Please try again.' })
      }
    }

    validateInvite()
  }, [token])

  const handleAccept = async () => {
    if (!token || state.status !== 'valid') return

    setState({ status: 'accepting' })

    try {
      const response = await api.orgs.acceptInvitation(token)
      setState({
        status: 'accepted',
        orgSlug: response.org.slug,
        orgName: response.org.name
      })

      // Redirect to org after a short delay
      setTimeout(() => {
        navigate(`/orgs/${response.org.slug}`)
      }, 2000)
    } catch (err: unknown) {
      console.error('Failed to accept invite:', err)
      const message = err instanceof Error ? err.message : 'Failed to accept invite'
      setState({
        status: 'error',
        error: message,
        details: state.status === 'valid' ? state.details : undefined,
      })
    }
  }

  const handleLogin = () => {
    // Redirect to login with return URL
    const returnUrl = `/invites/accept?token=${token}`
    navigate('/login', { state: { from: { pathname: returnUrl } } })
  }

  // Show loading state
  if (state.status === 'loading' || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-base)]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--text-secondary)] mx-auto" />
          <p className="mt-4 text-[var(--text-secondary)]">Validating invite...</p>
        </div>
      </div>
    )
  }

  // Show accepted state
  if (state.status === 'accepted') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-base)] px-4">
        <div className="max-w-md w-full text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
            Welcome to {state.orgName}!
          </h1>
          <p className="text-[var(--text-secondary)] mb-6">
            You have successfully joined the organization.
          </p>
          <p className="text-sm text-[var(--text-muted)]">
            Redirecting to dashboard...
          </p>
        </div>
      </div>
    )
  }

  // Show error state
  if (state.status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-base)] px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
              Invite Error
            </h1>
            <p className="text-[var(--text-secondary)]">
              {state.error}
            </p>
          </div>

          {state.details && (
            <div className="bg-[var(--bg-elevated)] rounded-lg border border-[var(--border-default)] p-4 mb-6">
              <p className="text-sm text-[var(--text-muted)] mb-2">Invite details:</p>
              <p className="text-[var(--text-primary)]">
                <strong>{state.details.org_name}</strong> ({state.details.role})
              </p>
              <p className="text-sm text-[var(--text-secondary)]">
                Sent to {state.details.email}
              </p>
            </div>
          )}

          <div className="text-center">
            <Link to="/">
              <Button variant="secondary">Go to Dashboard</Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // Show valid invite state
  const details = state.status === 'valid' ? state.details : null
  if (!details) return null

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-base)] px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-10 h-10 bg-[var(--accent)] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">u</span>
            </div>
            <span className="font-semibold text-xl text-[var(--text-primary)]">
              u.software
            </span>
          </div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">
            You're Invited
          </h1>
          <p className="mt-2 text-[var(--text-secondary)]">
            Join an organization on u.software
          </p>
        </div>

        {/* Invite Card */}
        <div className="bg-[var(--bg-elevated)] rounded-lg border border-[var(--border-default)] p-6 mb-6">
          <div className="flex items-start gap-4 mb-6">
            {details.org_logo_url ? (
              <img
                src={details.org_logo_url}
                alt={details.org_name}
                className="w-12 h-12 rounded-lg object-cover"
              />
            ) : (
              <div className="w-12 h-12 bg-[var(--bg-elevated-2)] rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[var(--text-secondary)]" />
              </div>
            )}
            <div>
              <h2 className="text-lg font-semibold text-[var(--text-primary)]">
                {details.org_name}
              </h2>
              <p className="text-sm text-[var(--text-secondary)]">
                {details.invited_by ? `Invited by ${details.invited_by}` : 'Organization invite'}
              </p>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--text-muted)]">Role</span>
              <span className="text-[var(--text-primary)] font-medium capitalize">
                {details.role}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--text-muted)]">Email</span>
              <span className="text-[var(--text-primary)]">
                {details.email}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--text-muted)]">Expires</span>
              <span className="text-[var(--text-primary)]">
                {new Date(details.expires_at).toLocaleDateString()}
              </span>
            </div>
          </div>

          {!isAuthenticated ? (
            <div className="space-y-3">
              <Alert variant="info">
                Please sign in to accept this invite. Make sure to use the email address: <strong>{details.email}</strong>
              </Alert>
              <Button
                onClick={handleLogin}
                className="w-full"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Sign in to Accept
              </Button>
            </div>
          ) : (
            <Button
              onClick={handleAccept}
              loading={state.status === 'accepting'}
              className="w-full"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {state.status === 'accepting' ? 'Joining...' : 'Accept Invite'}
            </Button>
          )}
        </div>

        <p className="text-center text-sm text-[var(--text-muted)]">
          By accepting, you'll join {details.org_name} as a {details.role}.
        </p>
      </div>
    </div>
  )
}
